import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Wordmark } from '@keobook/ui';
import type { EngagementLetterTemplate } from '@keobook/shared';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

export function EngagementLetterPage() {
  const navigate = useNavigate();
  const { currentClient, refresh } = useAuth();

  const [template, setTemplate] = useState<EngagementLetterTemplate | null>(null);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getEngagementLetterTemplate()
      .then((tpl) => {
        if (alive) setTemplate(tpl);
      })
      .catch((err: unknown) => {
        if (alive)
          setLoadError(
            err instanceof Error ? err.message : 'Could not load the engagement letter.',
          );
      });
    return () => {
      alive = false;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!template || !currentClient) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.acceptEngagementLetter(currentClient.id, {
        templateVersion: template.version,
        signatureFullName: signature.trim(),
        accepted: true,
      });
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'We could not record your acceptance.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container" style={{ maxWidth: 720 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" tagline="One quick agreement before we get started" />
        </div>

        <Card size="xl">
          <CardTitle>{template?.title ?? 'Engagement Letter'}</CardTitle>
          <CardSubtitle>
            Read the agreement, type your full legal name as your signature, and click{' '}
            <strong>I Agree</strong>. Required before any documents or bank accounts can be linked.
          </CardSubtitle>

          {loadError && (
            <div role="alert" className="kb-form-error">
              {loadError}
            </div>
          )}

          {!template ? (
            <p className="kb-muted">Loading agreement…</p>
          ) : (
            <>
              <div className="kb-letter-scroll" tabIndex={0} aria-label="Engagement letter body">
                <pre className="kb-letter-body">{template.body}</pre>
              </div>

              <form onSubmit={onSubmit} noValidate style={{ marginTop: '1.25rem' }}>
                <Field
                  label="Signature — type your full legal name"
                  required
                  hint={`Template version ${template.version}, effective ${template.effectiveDate}.`}
                >
                  <Input
                    value={signature}
                    onChange={(e) => setSignature(e.currentTarget.value)}
                    placeholder="First Middle Last"
                    minLength={2}
                    required
                    autoComplete="name"
                  />
                </Field>

                {submitError && (
                  <div role="alert" className="kb-form-error">
                    {submitError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, marginTop: '0.5rem' }}>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => navigate('/onboarding', { replace: true })}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button type="submit" block disabled={submitting || signature.trim().length < 2}>
                    {submitting ? 'Recording your acceptance…' : 'I Agree'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
